import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LayoutTemplate } from 'lucide-react';
import EmailTemplates, { emailTemplates, EmailTemplate } from './templates/EmailTemplates';
import SMSTemplates, { smsTemplates, SMSTemplate } from './templates/SMSTemplates';
import WhatsAppTemplates, { whatsAppTemplates, WhatsAppTemplate } from './templates/WhatsAppTemplates';

interface TemplateSelectorProps {
  campaignType: 'email' | 'sms' | 'whatsapp';
  onSelectTemplate: (template: EmailTemplate | SMSTemplate | WhatsAppTemplate) => void;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({ 
  campaignType, 
  onSelectTemplate 
}) => {
  const handleTemplateSelect = (template: EmailTemplate | SMSTemplate | WhatsAppTemplate) => {
    onSelectTemplate(template);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <LayoutTemplate className="h-4 w-4" />
          Use Template
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select a Template</DialogTitle>
        </DialogHeader>
        
        {campaignType === 'email' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {emailTemplates.map((template) => (
              <div 
                key={template.id} 
                className="border rounded-lg p-4 hover:border-primary hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => handleTemplateSelect(template)}
              >
                <div className="text-4xl mb-2">{template.thumbnail}</div>
                <h3 className="font-medium">{template.name}</h3>
                <p className="text-sm text-muted-foreground mb-2">{template.description}</p>
                <p className="text-xs text-muted-foreground truncate">Subject: {template.subject}</p>
              </div>
            ))}
          </div>
        )}
        
        {campaignType === 'sms' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {smsTemplates.map((template) => (
              <div 
                key={template.id} 
                className="border rounded-lg p-4 hover:border-primary hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => handleTemplateSelect(template)}
              >
                <div className="text-4xl mb-2">{template.thumbnail}</div>
                <h3 className="font-medium">{template.name}</h3>
                <p className="text-sm text-muted-foreground mb-2">{template.description}</p>
                <p className="text-xs text-muted-foreground truncate">{template.content.substring(0, 50)}...</p>
              </div>
            ))}
          </div>
        )}
        
        {campaignType === 'whatsapp' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {whatsAppTemplates.map((template) => (
              <div 
                key={template.id} 
                className="border rounded-lg p-4 hover:border-primary hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => handleTemplateSelect(template)}
              >
                <div className="text-4xl mb-2">{template.thumbnail}</div>
                <h3 className="font-medium">{template.name}</h3>
                <p className="text-sm text-muted-foreground mb-2">{template.description}</p>
                <p className="text-xs text-muted-foreground truncate">{template.content.substring(0, 50)}...</p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TemplateSelector; 